/** Shared contractor validation. No browser or server dependencies. */
export const normalizeLabel = (value) => typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";

/** Accept presentation spaces/hyphens only; never silently discard letters. */
export function registrationDigits(value) {
  return typeof value === "string" ? value.replace(/[\s-]/g, "") : "";
}

/** ABR modulus 89; ASIC ACN weighted check digit. Checks syntax, not registration status.
 * Sources: https://abr.business.gov.au/Help/AbnFormat
 * https://s3.ap-southeast-2.amazonaws.com/download.asic.gov.au/media/nvnh1kjr/msg_spec.pdf
 */
export function validRegistration(type, value) {
  const digits = registrationDigits(value);
  if (type === "ABN" && /^[1-9]\d{10}$/.test(digits)) {
    const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
    return [...digits].reduce((sum, digit, i) => sum + (Number(digit) - (i === 0 ? 1 : 0)) * weights[i], 0) % 89 === 0;
  }
  if (type === "ACN" && /^\d{9}$/.test(digits) && !/^0+$/.test(digits)) {
    const sum = [...digits.slice(0, 8)].reduce((total, digit, i) => total + Number(digit) * (8 - i), 0);
    return (10 - sum % 10) % 10 === Number(digits[8]);
  }
  return false;
}

export function contractorDisplayName(input) {
  return normalizeLabel(input.displayNameOverride) || (input.nameSource === "business"
    ? normalizeLabel(input.businessName)
    : [normalizeLabel(input.firstName), normalizeLabel(input.lastName)].filter(Boolean).join(" "));
}

/** Field-keyed errors are the same on both sides of the HTTP boundary. */
export function validateContractor(input, creating = false) {
  const errors = {};
  for (const [field, label] of [["firstName", "First name"], ["lastName", "Last name"]]) {
    if (!normalizeLabel(input[field])) errors[field] = `${label} is required.`;
  }
  if (typeof input.email !== "string" || input.email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email.trim())) errors.email = "Enter a valid email address.";
  for (const field of ["firstName", "lastName", "businessName", "displayNameOverride"]) {
    if (input[field] !== undefined && typeof input[field] !== "string") errors[field] = "Enter text for this field.";
    else if (normalizeLabel(input[field]).length > 200) errors[field] = "Use 200 characters or fewer.";
  }
  if (!["personal", "business"].includes(input.nameSource)) errors.nameSource = "Choose a display-name source.";
  if (input.nameSource === "business" && !normalizeLabel(input.businessName)) errors.businessName = "Business name is required when used as the name source.";
  if (!["ABN", "ACN"].includes(input.registrationType)) errors.registrationType = "Choose ABN or ACN.";
  if (!validRegistration(input.registrationType, input.registrationNumber)) errors.registrationNumber = `Enter a valid ${input.registrationType === "ACN" ? "9-digit ACN" : "11-digit ABN"}; check the number for a typo.`;
  if (!Array.isArray(input.serviceIds) || input.serviceIds.length === 0 || input.serviceIds.length > 250 || input.serviceIds.some((id) => typeof id !== "string" || id.length > 120)) errors.serviceIds = "Choose between 1 and 250 services.";
  if (creating && (typeof input.password !== "string" || input.password.length < 8 || new TextEncoder().encode(input.password).length > 72)) errors.password = "Use at least 8 characters and no more than 72 UTF-8 bytes.";
  if (creating && input.jobAccess !== undefined && !["none", "view", "full"].includes(input.jobAccess)) errors.jobAccess = "Choose a job access level.";
  return errors;
}
